/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  HeaderTitle,
  IconButton,
  LoaderWrapper,
  StyledContainer,
  TableCell,
  TableCellEmail,
  TableWrapper,
  TwoColumns,
} from '../../../services/settings/appStyle/global.style';

import {
  DeleteConfirmationModal,
  Loader,
  Switch,
  Table,
  IconButton as IconButtonWithText,
  TabButton,
} from '../../../Components/uielements';
import { IntlMessages } from '../../../helper/langHelper';
import { UserContainer } from '../UserV2/user.style';
import {
  friendList,
  removeFriend,
  toggleLocationShare,
  sendInvitationsList,
} from '../../../services/bioplus-services/friendList';
import { AddPlusIcon, DeleteIcon } from '../../../Components/icons';
import { InviteFriendModal } from '../UserV2/modals';
import GroupIcon from '@mui/icons-material/Group';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import GroupsIcon from '@mui/icons-material/Groups';
import moment from 'moment';
import Group from '../Group/group';
import GroupCreationModal from '../Group/modals/GroupCreationModal';
import { useUserContext } from '../../../hooks/UserContext';

// My Friends Component
const MyFriendList = () => {
  const friendsQuery = useQuery(['friendList'], () => friendList());
  const friendListData = friendsQuery?.data ?? [];
  const [deleteFriend, setDeleteFriend] = useState();
  const queryClient = useQueryClient();

  const mutation = useMutation((payload) => toggleLocationShare(payload), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('friendList');
    },
    onError: (error) => {
      alert(`Error: ${error?.response?.data?.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (friend) => {
      return removeFriend({ friendId: friend?.userId?._id });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries('friendList');
    },
    onError: (error) => {
      alert(`Error: ${error?.response?.data?.message}`);
    },
  });

  const handleToggleLocationShare = (friendId) => {
    mutation.mutate({ friendId });
  };

  const handleRemoveFriend = (friend) => {
    deleteMutation.mutate(friend);
    setDeleteFriend(undefined);
  };

  const friendColumns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.name" />,
        accessor: 'userName',
        Cell: (cellData) => (
          <TableCell>
            {`${cellData?.row?.original?.userId?.firstName} ${cellData?.row?.original?.userId?.lastName}`}
          </TableCell>
        ),
        width: '36%',
      },
      {
        Header: <IntlMessages id="table.col.email" />,
        accessor: 'email',
        width: '36%',
        Cell: (cellData) => (
          <TableCellEmail>{`${cellData?.row?.original?.userId?.email}`}</TableCellEmail>
        ),
      },
      {
        Header: <IntlMessages id="table.col.actions" defaultMessage="Actions" />,
        accessor: 'actions',
        isAction: true,
        width: '28%',
      },
    ],
    []
  );

  const actionList = [
    (data) => {
      return (
        <Switch
          checked={data?.toggleStatus || false}
          onClick={() => {
            handleToggleLocationShare(data?.userId?._id);
          }}
          style={{ width: '20px', height: '20px' }}
        />
      );
    },
    (data) => (
      <IconButton onClick={() => setDeleteFriend(data)}>
        <DeleteIcon style={{ width: '22px', height: '22px' }} />
      </IconButton>
    ),
  ];

  if (friendsQuery.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <>
      <TableWrapper>
        <Table
          data={friendListData}
          columns={friendColumns}
          hasFooter={false}
          actions={actionList}
        />
      </TableWrapper>
      {deleteFriend && (
        <DeleteConfirmationModal
          onClose={() => setDeleteFriend(undefined)}
          message={
            <IntlMessages
              id="friend.delete.confirmation"
              values={{
                friend: `${deleteFriend?.userId?.firstName} ${deleteFriend?.userId?.lastName}`,
              }}
            />
          }
          onConfirm={() => handleRemoveFriend(deleteFriend)}
        />
      )}
    </>
  );
};

// Send Invitations Component
const SendInvitationToBecomeFriend = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const invitationsQuery = useQuery(['sendInvitationsList', page, pageSize], () =>
    sendInvitationsList(page, pageSize)
  );
  const invitationsData = invitationsQuery?.data?.data ?? [];

  const invitationColumns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.name" />,
        accessor: 'userName',
        Cell: (cellData) => (
          <TableCell>
            {`${cellData?.row?.original?.userId?.firstName} ${cellData?.row?.original?.userId?.lastName}`}
          </TableCell>
        ),
        width: '20%',
      },
      {
        Header: <IntlMessages id="table.col.email" />,
        accessor: 'email',
        width: '20%',
        Cell: (cellData) => (
          <TableCellEmail>{`${cellData?.row?.original?.userId?.email}`}</TableCellEmail>
        ),
      },
      {
        Header: <IntlMessages id="table.col.phone" />,
        accessor: 'phone',
        width: '20%',
        Cell: (cellData) => (
          <TableCellEmail>{`${cellData?.row?.original?.userId?.phone}`}</TableCellEmail>
        ),
      },
      {
        Header: <IntlMessages id="table.col.role" defaultMessage="Role" />,
        accessor: 'role',
        width: '12%',
      },
      {
        Header: <IntlMessages id="table.col.status" defaultMessage="Status" />,
        accessor: 'status',
        width: '12%',
      },
      {
        Header: <IntlMessages id="table.col.createdAt" />,
        accessor: 'createdAt',
        width: '20%',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('DD-MM-YYYY') : '--'}
          </TableCell>
        ),
      },
    ],
    []
  );

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const actionList = [];

  if (invitationsQuery.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <TableWrapper>
      <Table
        data={invitationsData}
        columns={invitationColumns}
        actions={actionList}
        hasFooter={true}
        page={page}
        pageSize={pageSize}
        prevPage={handlePreviousPage}
        nextPage={handleNextPage}
        total={invitationsQuery?.data?.total || 0}
      />
    </TableWrapper>
  );
};

// Main FriendList Component
const FriendList = () => {
  const [isInviteVisitorModal, setIsInviteVisitorModal] = useState(false);
  const [isOpenGroupCreation, setIsOpenGroupCreation] = useState(false);
  const [selectedTab, setSelectedTab] = useState('My Friends');

  const userContext = useUserContext();
  const { userPermission } = userContext;
  const isGroupAllowToManage = userPermission?.find((permission) => permission?.label === 'group');

  // Permission flags derived from the "group" permission object
  const canCreateGroup = !!isGroupAllowToManage?.isCreate;
  const canViewGroup = !!isGroupAllowToManage?.isView;

  const handleClosedInviteVisitorModal = () => {
    setIsInviteVisitorModal(false);
  };

  const handleSelectedTab = (selectedTab) => {
    setSelectedTab(selectedTab);
  };

  return (
    <StyledContainer>
      <UserContainer>
        <HeaderTitle>
          <IntlMessages id="user.module.friends" />
        </HeaderTitle>
        <TwoColumns style={{ gap: '10px' }}>
          <TabButton
            buttonArray={[
              { buttonText: 'My Friends', textId: '1', icon: <GroupIcon /> },
              // { buttonText: 'Pending', textId: '2', icon: <GroupAddIcon /> },
              ...(canViewGroup
                ? [{ buttonText: 'Groups', textId: '3', icon: <GroupsIcon /> }]
                : []),
            ]}
            onButtonClick={handleSelectedTab}
            selectedTab={selectedTab}
          />
          {selectedTab !== 'Groups' && (
            <IconButtonWithText
              text={<IntlMessages id="nav.add.friend" />}
              icon={<AddPlusIcon />}
              onClick={() => {
                setIsInviteVisitorModal(true);
              }}
            />
          )}
          {selectedTab === 'Groups' && canCreateGroup && (
            <IconButtonWithText
              text={<IntlMessages id="button.label.group.creation" />}
              icon={<AddPlusIcon />}
              onClick={() => {
                setIsOpenGroupCreation(true);
              }}
            />
          )}
        </TwoColumns>
      </UserContainer>

      {selectedTab === 'My Friends' ? (
        <MyFriendList />
      ) : selectedTab === 'Groups' ? (
        <Group />
      ) : (
        <SendInvitationToBecomeFriend />
      )}

      {isInviteVisitorModal && <InviteFriendModal onClose={handleClosedInviteVisitorModal} />}
      {isOpenGroupCreation && <GroupCreationModal onClose={() => setIsOpenGroupCreation(false)} />}
    </StyledContainer>
  );
};

export default FriendList;
